import React from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Notifications as AlertsIcon,
  Email as EmailIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Computer as DesktopIcon,
  AccountBalance as BillingIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';
import AlertStatus from '../ui/AlertStatus';
import { SystemStatus, EmergencyControls } from '../../types/dashboard';
import { useDesktopTier } from '../../hooks/useDesktopTier';

export interface AppBarProps {
  systemStatus: SystemStatus;
  emergencyControls: EmergencyControls;
  onEmergencyStop: () => void;
  onEmergencyReactivate: () => void;
  onThemeToggle: () => void;
  onLogout: () => void;
  isDarkMode: boolean;
  lowConfidenceCount: number;
}

const AppBar: React.FC<AppBarProps> = ({
  systemStatus,
  emergencyControls,
  onEmergencyStop,
  onEmergencyReactivate,
  onThemeToggle,
  onLogout,
  isDarkMode,
  lowConfidenceCount,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { isDesktopTier } = useDesktopTier();

  // Define all navigation items
  const allNavigationItems = [
    { key: 'home', label: 'Home', icon: <HomeIcon />, path: '/', restricted: false },
    { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', restricted: false },
    { key: 'alerts', label: 'Alerts', icon: <AlertsIcon />, path: '/alerts', restricted: true },
    { key: 'email', label: 'Email', icon: <EmailIcon />, path: '/email', restricted: true },
    { key: 'webhook-activity', label: 'Webhook Activity', icon: <WebhookIcon />, path: '/webhook-activity', restricted: true },
    { key: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/settings', restricted: false },
    { key: 'support', label: 'Support', icon: <SupportIcon />, path: '/support', restricted: false },
  ];

  // Desktop tier specific navigation items
  const desktopTierItems = [
    { key: 'home', label: 'Desktop App', icon: <DesktopIcon />, path: '/', restricted: false },
    { key: 'settings', label: 'Subscription', icon: <SettingsIcon />, path: '/settings', restricted: false },
    { key: 'billing', label: 'Billing', icon: <BillingIcon />, path: '/settings', restricted: false },
    { key: 'support', label: 'Support', icon: <SupportIcon />, path: '/', restricted: false },
  ];

  // Filter navigation items based on desktop tier
  const navigationItems = isDesktopTier
    ? desktopTierItems
    : allNavigationItems.filter(item => !item.restricted || !isDesktopTier);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    if (isMobile) setMobileOpen(false);
    navigate(path);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Toolbar>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/BranchRouterLogo.jpg`}
          alt="Branch Router logo"
          sx={{ width: 28, height: 28, borderRadius: 0.75, mr: 1.5 }}
        />
        <Typography variant="h6" noWrap component="div">
          Branch Router
        </Typography>
      </Toolbar>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => handleNavigation(item.path)}
              selected={isActive(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '20',
                  '&:hover': { backgroundColor: theme.palette.primary.main + '30' },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              {item.key === 'email' && lowConfidenceCount > 0 && (
                <Badge badgeContent={lowConfidenceCount} color="warning" />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <MuiAppBar position="static" elevation={0}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL || ''}/BranchRouterLogo.jpg`}
              alt="Branch Router logo"
              sx={{ width: 32, height: 32, borderRadius: 0.75, mr: 1.5, flexShrink: 0 }}
            />
            <Typography variant="h6" component="div" noWrap>
              Branch Router
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.key}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color: isActive(item.path) ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  }}
                >
                  {item.icon}
                  <Box sx={{ ml: 1 }}>{item.label}</Box>
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* System Status */}
            <AlertStatus
              status={systemStatus.status === 'healthy' ? 'success' : 
                     systemStatus.status === 'warning' ? 'warning' : 
                     systemStatus.status === 'error' ? 'error' : 'inactive'}
              executionTime={
                systemStatus.executionTime && systemStatus.executionTime > 0
                  ? systemStatus.executionTime
                  : undefined
              }
              size="small"
            />

            {/* Emergency Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {emergencyControls.isStopped ? (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<PlayIcon />}
                  onClick={onEmergencyReactivate}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Reactivate
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={onEmergencyStop}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Emergency Stop
                </Button>
              )}
            </Box>

            {/* Theme Toggle */}
            <ThemeToggle
              isDark={isDarkMode}
              onToggle={onThemeToggle}
              size="small"
            />

            {/* Logout Button */}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={() => {
                console.log('Logout button clicked in AppBar');
                onLogout();
              }}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </MuiAppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default AppBar;
